import UserGuide from '@/components/help/UserGuide';
import type { ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMobileDialogViewport } from '@/hooks/useMobileDialogViewport';

export default function WorkModalShell({
  open,
  onClose,
  title,
  busy = false,
  dirty = false,
  children,
  wide = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  busy?: boolean;
  dirty?: boolean;
  children: ReactNode;
  wide?: boolean;
}) {
  const viewport = useMobileDialogViewport(open);
  const close = () => {
    if (busy) return;
    if (!dirty || window.confirm('Discard your unsaved changes?')) onClose();
  };
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
          data-field-guide-scope
        />
        <Dialog.Content
          data-work-modal
          aria-describedby={undefined}
          style={viewport}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            close();
          }}
          onInteractOutside={(event) => {
            event.preventDefault();
            // A nested work dialog (for example print privacy) owns this interaction.
            if (event.target instanceof Element && event.target.closest('[data-work-modal]'))
              return;
            close();
          }}
          className={`fixed inset-x-0 top-[var(--dialog-mobile-top,0px)] z-50 flex h-[var(--dialog-mobile-height,100dvh)] min-w-0 flex-col overflow-hidden bg-card p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl sm:inset-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:max-h-[90dvh] sm:w-[calc(100vw-3rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:border sm:border-border sm:p-5 ${wide ? 'sm:max-w-2xl' : 'sm:max-w-lg'}`}
        >
          <div className="flex items-start justify-between gap-3 pr-8">
            <Dialog.Title className="sr-only">{title}</Dialog.Title>
            <UserGuide compact />
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
